/** @module components/drag-and-drop-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View, {ViewRegistrationMap} from '@lernetz/ts-lib/src/core/view/View';

import Sortable from 'sortablejs';

import {Quiz, QuizInputState, QuizVisitor} from '../quiz';
import {DropArea, DropAreaView} from './DropArea';
import {LabelledDropArea, LabelledDropAreaView} from './LabelledDropArea';
import {Item} from './Item';
import {TextItem, TextItemView} from './TextItem';

import {DragAndDropQuiz as dragAndDropQuizTemplate,
        TextItem as textItemTemplate} from '../../templates/drag-and-drop-quiz';


/**
 * [[QuizVisitor]]s that support [[DragAndDropQuiz]] should implement this interface.
 */
export interface DragAndDropQuizVisitor<TResult = void> extends QuizVisitor<TResult> {
    visitDragAndDropQuiz(quiz: DragAndDropQuiz): TResult;
}


function isDragAndDropQuizVisitor<TResult>(visitor: QuizVisitor<TResult>): visitor is DragAndDropQuizVisitor<TResult> {
    return typeof visitor['visitDragAndDropQuiz'] !== 'undefined';
}


/**
 * Specifies the initial state for a [[DragAndDropQuiz]].
 */
export interface DragAndDropQuizInitialState {

    /**
     * Sets the available drop areas for the drag'n'drop quiz.
     */
    dropAreas: DropArea[];

    /**
     * A function which maps each [[Item]] to the [[DropArea]] where they should be placed
     * for the quiz to be correctly solved.
     */
    getCorrectDropAreaFor(item: Item): DropArea;
}


/**
 * A drag'n'drop quiz.
 *
 * **Warning:** The drag'n'drop functionality is based on the [Sortable](https://github.com/SortableJS/Sortable)
 * library, which you will need to load manually in your application's HTML document!
 *
 * @example
 * ```typescript
 *
 * // Define a drop area in which all items are initially placed:
 * const pool = new DropArea({
 *     items: [
 *         new TextItem({ text: 'cat' }),
 *         new TextItem({ text: 'apple' }),
 *         new TextItem({ text: 'dog' }),
 *         new TextItem({ text: 'orange' }),
 *         new TextItem({ text: 'bread' }),
 *     ]
 * });
 *
 * // Define two empty (target) drop areas:
 * const animals = new LabelledDropArea({ label: 'Drop animals here.', items: [] });
 * const fruit = new DropArea({ label: 'Drop fruit here.', items: [] });
 *
 * // Use the above drop areas when defining the quiz:
 * const dragAndDropQuiz = new DragAndDropQuiz({
 *     dropAreas: [pool, animals, fruit],
 *     getCorrectDropAreaFor(item: TextItem) {
 *         switch (item.text) {
 *             case 'cat':
 *             case 'dog':
 *                 return animals;
 *             case 'apple':
 *             case 'orange':
 *                 return fruit;
 *              default:
 *                 return pool;
 *         }
 *     }
 * });
 * ```
 */
export class DragAndDropQuiz extends Model implements Quiz {

    private readonly dropAreas_: DropArea[];
    private readonly getCorrectDropAreaFor_: (item: Item) => DropArea;

    /**
     * Initializes a new instance of the [[DragAndDropQuiz]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: DragAndDropQuizInitialState, data: object = {}) {
        super(data);
        this.dropAreas_ = initialState.dropAreas;
        this.getCorrectDropAreaFor_ = initialState.getCorrectDropAreaFor;

        this.updateInputState();
        this.updateItems();
        this.set('inputState', 'none');
        for (const dropArea of this.dropAreas) {
            const onItemsChange = dropArea.change.filter(ch => ch.name === 'items');
            onItemsChange.subscribe(this.updateInputState.bind(this));
            onItemsChange.subscribe(this.updateItems.bind(this));
        }
    }

    /**
     * Gets all drop areas in the quiz.
     */
    public get dropAreas(): DropArea[] {
        return this.dropAreas_;
    }

    /** @inheritdoc */
    public get inputState(): QuizInputState {
        return this.get<QuizInputState>('inputState');
    }

    /**
     * Dispatches to [[DragAndDropQuizVisitor.visitDragAndDropQuiz]] (if implemented by `visitor`).
     */
    public accept<TResult = void>(visitor: QuizVisitor<TResult>): TResult {
        if (isDragAndDropQuizVisitor(visitor)) {
            return visitor.visitDragAndDropQuiz(this);
        }
    }

    /**
     * Gets a CSS selector identifying the template element where drop zones should be rendered.
     * The default selector returned is `.dropAreas`.
     *
     * Override this method if your quiz' template needs to have drop zones rendered in a
     * different place.
     */
    public getDropAreaContainerSelector(): string {
        return '.dropAreas';
    }

    /**
     * Updates the [[inputState]] property.
     */
    protected updateInputState(): void {
        for (const dropArea of this.dropAreas) {
            for (const item of dropArea.items) {
                if (this.getCorrectDropAreaFor_(item) !== dropArea) {
                    this.set('inputState', 'incorrect');
                    return;
                }
            }
        }
        this.set('inputState', 'correct');
    }

    /**
     * Updates the state of each item in the quiz.
     * Notably, this should update the [[Item.isInCorrectDropArea]] property.
     */
    protected updateItems(): void {
        for (const dropArea of this.dropAreas) {
            for (const item of dropArea.items) {
                item.inCorrectDropArea = this.getCorrectDropAreaFor_(item) === dropArea;
            }
        }
    }
}


/**
 * A `View` component that renders a [[DragAndDropQuiz]].
 */
export class DragAndDropQuizView extends View<DragAndDropQuiz> {

    private static nextId_: number;

    private readonly id_: number;

    /**
     * Initializes a new instance of the [[DragAndDropQuizView]] class.
     */
    public constructor() {
        super({
            template: dragAndDropQuizTemplate,
        });
        this.id_ = ++DragAndDropQuizView.nextId_;
    }

    /** @inheritdoc */
    protected init() {

        // we need these to map from DOM elements back to view models:
        const dropAreas = new Map<Node, DropArea>();
        const items = new Map<Node, Item>();

        const dropAreaContainerNode = this.node.querySelector(this.data.getDropAreaContainerSelector());
        for (const dropArea of this.data.dropAreas) {

            // Render one drop area view:
            const dropAreaView = this.createChild(dropArea);
            dropAreaView.data = dropArea;
            dropAreaView.render();

            // Add it into the drop area container:
            const dropAreaNode = dropAreaView.node;
            dropAreaContainerNode.appendChild(dropAreaNode);

            // Link the drop area with its draggable item container node:
            const draggableItemContainerNode = <HTMLElement>dropAreaNode.querySelector(dropArea.getItemContainerSelector());
            dropAreas.set(draggableItemContainerNode, dropArea);

            for (const item of dropArea.items) {

                // Render one draggable item view:
                const itemView = dropAreaView.createChild(item);
                itemView.data = item;
                itemView.render();

                // Add it to the drop area's draggable item container:
                const itemNode = itemView.node;
                draggableItemContainerNode.appendChild(itemNode);

                // Link the draggable item with its node:
                items.set(itemNode, item);
            }

            // Finally, register the draggable item nodes with Sortable
            // which makes them actually drag'n'droppable:
            Sortable.create(draggableItemContainerNode, {
                group: this.id_.toString(),
                onAdd(event) {
                    const item = items.get(event.item);
                    dropAreas.get(event.from).onItemRemoved(item);
                    dropAreas.get(event.to).onItemDropped(item);
                },
                onMove(event) {
                    const item = items.get(event.dragged);
                    if (event.from !== event.to) {
                        const target = dropAreas.get(event.to);
                        if (!target.canDropItem(item)) {
                            return false;
                        }
                    }
                }
            });
        }
    }
}


/**
 * A set of view registrations for [[ChoiceQuiz]].
 */
export const registrations = new ViewRegistrationMap();
registrations.set(DragAndDropQuiz, DragAndDropQuizView);
registrations.set(DropArea, () => new DropAreaView());
registrations.set(LabelledDropArea, LabelledDropAreaView);
registrations.set(TextItem, TextItemView);
